/**
 * Theme Provider - White-label theming + Dark Mode
 * Applies tenant-specific theme and manages dark/light mode
 */

import { createContext, useContext, useEffect, useState } from "react";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { resolveTenantId, loadTenantConfig, applyTenantTheme, TenantConfig } from "@/lib/tenant";

interface ThemeContextType {
  tenant: TenantConfig | null;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  tenant: null,
  isLoading: true,
});

export function useTenantTheme() {
  return useContext(ThemeContext);
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function initTheme() {
      try {
        const tenantId = resolveTenantId();
        
        // Cache em sessionStorage para evitar recálculos
        const cacheKey = `tenant:${tenantId}`;
        const cached = sessionStorage.getItem(cacheKey);
        
        let tenantConfig: TenantConfig;
        if (cached) {
          tenantConfig = JSON.parse(cached);
        } else {
          tenantConfig = await loadTenantConfig(tenantId);
          sessionStorage.setItem(cacheKey, JSON.stringify(tenantConfig));
        }
        
        setTenant(tenantConfig);
        
        // Aplicar tema em batch (próximo frame) para evitar bloquear React tree
        requestAnimationFrame(() => {
          applyTenantTheme(tenantConfig.theme);
        });
      } catch (error) {
        console.error("Failed to load tenant config:", error);
      } finally {
        setIsLoading(false);
      }
    }

    initTheme();
  }, []);

  return (
    <ThemeContext.Provider value={{ tenant, isLoading }}>
      <NextThemesProvider 
        attribute="class" 
        defaultTheme="system" 
        enableSystem
        storageKey="theme"
        forcedTheme={undefined}
        enableColorScheme={false}
      >
        {children}
      </NextThemesProvider>
    </ThemeContext.Provider>
  );
}
